import { alpha } from "@mui/material/styles";
import { themeConstants } from "../../theme";

/** Matches `MuiCard` / Mi contenido stat cards (`theme.palette.divider`, 8px radius, light shadow). */
export const REPORT_WEB_CARD_BORDER = themeConstants.border.color;

export const reportWebCardSx = {
  border: `${themeConstants.border.width} solid ${REPORT_WEB_CARD_BORDER}`,
  borderRadius: themeConstants.border.radius,
  boxShadow: `0 1px 2px ${alpha("#1A1930", 0.08)}`,
  boxSizing: "border-box",
} as const;

/** Visual tokens aligned with metrics PDF (`metricsPdf/`). */
export const REPORT_WEB = {
  pageWidthPx: 1280,
  pageMinHeightPx: 720,
  paddingXPx: 60,
  paddingTopPx: 44,
  paddingBottomPx: 48,
  /** Deck-style pages (period summary, ranking, etc.) */
  deckPaddingXPx: 72,
  headerHeightPx: 90,
  footerHeightPx: 56,
  /** Deck page header — title block + bottom divider */
  pageHeaderPaddingTopPx: 44,
  pageHeaderPaddingBottomPx: 20,
  pageHeaderDateFontPx: 12,
  footerTextFontPx: 12,
  footerWordmarkHeightPx: 12,
  /** Shared content area inset below page header (all deck pages). */
  pageContentPaddingYPx: 28,
  /** Program / channel deck pages — less top inset below the header. */
  pageContentProgramPaddingTopPx: 12,
  pageContentSectionGapPx: 24,
  brandPrimary: "#131116",
  textBody: "#1A1A1A",
  textMuted: "#666666",
  textMutedAlt: "#6B7280",
  labelMuted: "#9aa0a6",
  borderLight: "#E0E0E0",
  /** Page chrome dividers; card borders use {@link REPORT_WEB_CARD_BORDER}. */
  borderDivider: REPORT_WEB_CARD_BORDER,
  headerBg: "#F5F5F5",
  rowDivider: REPORT_WEB_CARD_BORDER,
  totalBg: "#FAFAFA",
  chartFill: "#F9FAFB",
  chartLine: "#131116",
  chartPeak: "#9CA3AF",
  logoGradient: "linear-gradient(135deg, #131116, #3a3742)",
} as const;

export type ReportWebComparisonGranularity = "week" | "month";

export type ReportWebComparisonPeriodLabel = "semana" | "mes" | "período";

export function reportWebComparisonPeriodLabel(
  granularity?: ReportWebComparisonGranularity,
): ReportWebComparisonPeriodLabel {
  if (granularity === "week") return "semana";
  if (granularity === "month") return "mes";
  return "período";
}

/**
 * Week/month copy ("Semana del…", "vs semana") only applies to Predefinido
 * periods. Manual date ranges always use neutral "período" wording.
 */
export function comparisonGranularityForPeriodMode(
  periodMode: "fixed" | "free",
  periodGranularity: ReportWebComparisonGranularity,
): ReportWebComparisonGranularity | undefined {
  return periodMode === "fixed" ? periodGranularity : undefined;
}

/** Secondary caption — KPI deltas, platform card labels, etc. */
export const reportWebCaptionSx = {
  fontSize: 12,
  color: REPORT_WEB.labelMuted,
  fontWeight: 500,
} as const;

/** Section headings — e.g. Totales del período, Desglose por plataforma */
export const reportWebSectionTitleSx = {
  fontSize: 16,
  fontWeight: 700,
  color: REPORT_WEB.brandPrimary,
  letterSpacing: "-0.01em",
} as const;

/** Page header title — matches Resumen del período */
export const reportWebPageTitleSx = {
  fontSize: 30,
  fontWeight: 700,
  color: REPORT_WEB.brandPrimary,
  letterSpacing: "-0.015em",
  lineHeight: 1.15,
} as const;

export const reportWebPageHeaderDateSx = {
  fontSize: REPORT_WEB.pageHeaderDateFontPx,
  fontWeight: 500,
  color: REPORT_WEB.textMutedAlt,
  lineHeight: 1.3,
  whiteSpace: "nowrap",
  flexShrink: 0,
} as const;

export const reportWebFooterTextSx = {
  fontSize: REPORT_WEB.footerTextFontPx,
  color: REPORT_WEB.textMutedAlt,
  lineHeight: 1.35,
} as const;

/** Content slot below page header — same horizontal + vertical padding on every deck page. */
export const reportWebPageContentSx = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  px: `${REPORT_WEB.deckPaddingXPx}px`,
  py: `${REPORT_WEB.pageContentPaddingYPx}px`,
  /** Fill the padded slot without height:100% (which ignores py and clips the bottom). */
  "& > *": {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
} as const;

/** Platform card title — e.g. YouTube Videos, Género, Edad */
export const reportWebPlatformCardTitleSx = {
  fontSize: 12,
  fontWeight: 700,
  color: REPORT_WEB.brandPrimary,
  lineHeight: 1.2,
} as const;

export const reportWebPlatformCardSx = {
  ...reportWebCardSx,
  p: "12px 14px",
  minWidth: 0,
} as const;

export const reportWebPlatformCardHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  pb: "10px",
  borderBottom: `${themeConstants.border.width} solid ${REPORT_WEB_CARD_BORDER}`,
  mb: "10px",
} as const;

/** Platform card row labels — e.g. Prom. en vivo, Views */
export const reportWebPlatformRowLabelSx = {
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.08em",
  color: REPORT_WEB.textMutedAlt,
  textTransform: "uppercase",
} as const;

export const PODSCOPE_WORDMARK_SRC = `${process.env.PUBLIC_URL ?? ""}/metrics-pdf/podscope-wordmark-dark.png`;

export const PODSCOPE_SQUARE_SRC = `${process.env.PUBLIC_URL ?? ""}/metrics-pdf/podscope-square.png`;

export const tabularNumsSx = {
  fontFeatureSettings: '"tnum"',
  fontVariantNumeric: "tabular-nums",
} as const;

export function companyInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}
